import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { BarChart2, FileText, RefreshCw } from 'lucide-react'
import { apiFetch } from '../../config/api'
import { type PageStat, StatTable } from './statistikShared'
import PageHeader from '../../components/Dashboard/PageHeader'

export default function DashboardStatistik() {
  const [pages, setPages] = useState<PageStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  const fetchStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/analytics/pages', {
        headers: { Authorization: getAuthToken() },
      })
      if (!res.ok) {
        if (res.status === 401) return
        throw new Error('Statistiken konnten nicht geladen werden.')
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.pages)) {
        setPages(
          data.pages.map((p: { path: string; today: string | number; last7d: string | number }) => ({
            path: p.path || '/',
            today: String(p.today ?? 0),
            last7d: String(p.last7d ?? 0),
          }))
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const totalToday = pages.reduce((sum, p) => sum + parseInt(p.today, 10) || 0, 0)
  const total7d = pages.reduce((sum, p) => sum + parseInt(p.last7d, 10) || 0, 0)
  const max7d = Math.max(1, ...pages.map((p) => parseInt(p.last7d, 10) || 0))

  const other = useMemo(() => {
    return pages.filter((p) => {
      const path = p.path || ''
      return !path.startsWith('/partys') && !path.startsWith('/brunch')
    })
  }, [pages])

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        title="Statistiken"
        description="Gesamtübersicht und Aufrufe anderer Seiten."
        actions={
          <button
            onClick={fetchStats}
            disabled={loading}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-800 bg-gray-950 text-sm text-zinc-300 hover:bg-gray-900 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        }
      />

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-lg border border-gray-800 bg-gray-950 p-4">
          <p className="text-xs font-medium text-zinc-500 mb-2">Heute</p>
          <p className="text-2xl font-semibold text-white tabular-nums">{loading ? '—' : totalToday}</p>
          <p className="text-xs text-zinc-500 mt-1">Seitenaufrufe</p>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-950 p-4">
          <p className="text-xs font-medium text-zinc-500 mb-2">Letzte 7 Tage</p>
          <p className="text-2xl font-semibold text-white tabular-nums">{loading ? '—' : total7d}</p>
          <p className="text-xs text-zinc-500 mt-1">Seitenaufrufe</p>
        </div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400"
        >
          {error}
        </motion.div>
      )}

      {/* Alle anderen Seiten */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl border border-white/10 overflow-hidden min-w-0"
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10 flex items-center gap-3">
          <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
          <h2 className="text-sm font-semibold text-white truncate">Andere Seiten</h2>
        </div>

        {loading ? (
          <div className="p-8 sm:p-12 text-center text-gray-400">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
            <p>Statistiken werden geladen …</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="p-8 sm:p-12 text-center text-gray-400">
            <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Noch keine Aufrufdaten vorhanden.</p>
            <p className="text-sm mt-1">Sobald Besucher die Website nutzen, erscheinen hier die Zahlen.</p>
          </div>
        ) : other.length === 0 ? (
          <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
            Keine weiteren Seiten mit Aufrufen.
          </div>
        ) : (
          <div className="overflow-x-auto min-w-0 w-full">
            <StatTable rows={other} maxBar={max7d} startIndex={0} />
          </div>
        )}
      </motion.div>
    </div>
  )
}
